import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Badge,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  Warning as WarningIcon,
  PriorityHigh as PriorityHighIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { inventoryAPI } from '../../utils/api';

const LowStockWidget = ({ onNotify, onViewAll }) => {
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch low stock items
  const fetchLowStockItems = async () => {
    setLoading(true);
    try {
      const response = await inventoryAPI.getLowStock();
      setLowStockItems(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching low stock items:', err);
      setError('Failed to load low stock data');
      
      // Set demo data for development
      const demoLowStock = [
        { _id: '1', name: 'Test Tubes', quantity: 3, minThreshold: 10, department: 'Chemistry' },
        { _id: '2', name: 'RAM Modules', quantity: 2, minThreshold: 5, department: 'Computer Science' },
        { _id: '3', name: 'Beakers', quantity: 4, minThreshold: 15, department: 'Chemistry' },
      ];
      setLowStockItems(demoLowStock);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLowStockItems();
  }, []);

  // Calculate severity level for low stock items
  const getLowStockSeverity = (item) => {
    const percentage = (item.quantity / item.minThreshold) * 100;
    if (percentage <= 30) return 'critical';
    if (percentage <= 60) return 'warning';
    return 'normal';
  };

  // Group items by severity
  const criticalItems = lowStockItems.filter(item => getLowStockSeverity(item) === 'critical');
  const warningItems = lowStockItems.filter(item => getLowStockSeverity(item) === 'warning');

  // Format the last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never';
    return lastUpdated.toLocaleTimeString();
  };

  // Handle refresh button click
  const handleRefresh = () => {
    fetchLowStockItems();
  };

  // Handle notify button click
  const handleNotify = () => {
    if (onNotify) {
      onNotify(lowStockItems);
    }
  };

  // Handle view all button click
  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Badge badgeContent={lowStockItems.length} color="error" sx={{ mr: 1 }}>
              <TrendingDownIcon color="warning" />
            </Badge>
            <Typography variant="h6">Low Stock Items</Typography>
          </Box>
        }
        action={
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
        subheader={`Last updated: ${formatLastUpdated()}`}
      />
      <Divider />
      <CardContent sx={{ flex: '1 0 auto', overflowY: 'auto', p: 0 }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <CircularProgress size={24} sx={{ mb: 1 }} />
            <Typography variant="body2">Loading low stock items...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : lowStockItems.length === 0 ? (
          <Alert severity="success" sx={{ m: 2 }}>All inventory items are above threshold levels.</Alert>
        ) : (
          <>
            {criticalItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ bgcolor: 'error.light', p: 1, display: 'flex', alignItems: 'center' }}>
                  <PriorityHighIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="error.dark">
                    Critical Stock Items ({criticalItems.length})
                  </Typography>
                </Box>
                <List dense>
                  {criticalItems.slice(0, 3).map((item) => (
                    <ListItem key={item._id} divider>
                      <ListItemText 
                        primary={item.name}
                        secondary={`Qty: ${item.quantity} / Min: ${item.minThreshold} (${item.department})`}
                      />
                      <Chip 
                        label={`${((item.quantity / item.minThreshold) * 100).toFixed(0)}%`}
                        color="error"
                        size="small"
                        sx={{ minWidth: 45 }}
                      />
                    </ListItem>
                  ))}
                  {criticalItems.length > 3 && (
                    <ListItem>
                      <Typography variant="body2" color="text.secondary">
                        +{criticalItems.length - 3} more critical items
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
            
            {warningItems.length > 0 && (
              <Box>
                <Box sx={{ bgcolor: 'warning.light', p: 1, display: 'flex', alignItems: 'center' }}>
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="warning.dark">
                    Warning Stock Items ({warningItems.length})
                  </Typography>
                </Box>
                <List dense>
                  {warningItems.slice(0, 3).map((item) => (
                    <ListItem key={item._id} divider>
                      <ListItemText 
                        primary={item.name}
                        secondary={`Qty: ${item.quantity} / Min: ${item.minThreshold} (${item.department})`}
                      />
                      <Chip 
                        label={`${((item.quantity / item.minThreshold) * 100).toFixed(0)}%`}
                        color="warning"
                        size="small"
                        sx={{ minWidth: 45 }}
                      />
                    </ListItem>
                  ))}
                  {warningItems.length > 3 && (
                    <ListItem>
                      <Typography variant="body2" color="text.secondary">
                        +{warningItems.length - 3} more warning items
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
          </>
        )}
      </CardContent>
      <Divider />
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          startIcon={<NotificationsIcon />} 
          size="small" 
          color="primary"
          onClick={handleNotify}
          disabled={lowStockItems.length === 0 || loading}
        >
          Notify
        </Button>
        <Button 
          endIcon={<SendIcon />} 
          size="small" 
          color="secondary"
          onClick={handleViewAll}
        >
          View All
        </Button>
      </Box>
    </Card>
  );
};

export default LowStockWidget;